import {
  ReactNode,
  FC,
  useEffect,
  useRef,
  forwardRef,
  useCallback,
  type ComponentProps,
} from 'react'

import { Navigate, useLocation, useNavigate, useParams } from 'react-router'

import { useDomain } from '@tribeplatform/react-components/common/hooks'
import { Link } from '@tribeplatform/react-components/Link'
import {
  TribeEmitter,
  TribeEmitterEvent,
  TribeRouter,
  useAnalytics,
  type TribeRouterType,
} from '@tribeplatform/react-sdk'
import { useAuthMember } from '@tribeplatform/react-sdk/hooks'

import type { OTSM } from '../server/types/server.types.js'

import { Otsm } from './components/Otsm.js'
import { useMsClarity } from './hooks/useMsClarity.js'
import { useUpdateTimeZone } from './hooks/useUpdateTimeZone.js'
import { useWindowTribe } from './hooks/useWindowTribe.js'
import { useSegment } from './providers/SegmentProvider.js'

type TribeWebProps = {
  otsm: OTSM
  children: ReactNode
}

export const TribeWeb: FC<TribeWebProps> = ({ otsm, children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const previousPage = useRef<string>(location.pathname)

  useWindowTribe()
  useUpdateTimeZone()
  const { subfolder } = useDomain()
  useMsClarity()

  const analytics = useAnalytics()
  const segment = useSegment()
  const { data: authMember, isGuest } = useAuthMember()
  const lastIdentifiedKey = useRef<string | null>(null)

  useEffect(() => {
    analytics.trackPageView(location.pathname)
    segment.page?.()
  }, [location.pathname, analytics, segment])

  useEffect(() => {
    if (isGuest || !authMember?.id) {
      if (lastIdentifiedKey.current) {
        lastIdentifiedKey.current = null
        segment.reset?.()
      }
      return
    }
    if (!authMember.email) {
      return
    }
    const key = `${authMember.id}:${authMember.email}:${authMember.name ?? ''}`
    if (lastIdentifiedKey.current === key) {
      return
    }
    const isFirstIdentify = lastIdentifiedKey.current === null
    lastIdentifiedKey.current = key
    if (isFirstIdentify) {
      segment.alias?.(authMember.id, authMember.email)
    }
    segment.identify?.({
      id: authMember.id,
      email: authMember.email,
      name: authMember.name,
    })
  }, [isGuest, authMember?.id, authMember?.email, authMember?.name, segment])

  // emit an event on route change
  useEffect(() => {
    const page = location.pathname
    TribeEmitter.emit(TribeEmitterEvent.PAGE_VIEW, {
      page,
      previousPage:
        previousPage.current !== page ? previousPage.current : undefined,
    })

    previousPage.current = page
  }, [location.pathname])

  // clear listeners on component unmount
  useEffect(
    () => () => {
      TribeEmitter.clearListeners(TribeEmitterEvent.PAGE_VIEW)
    },
    [],
  )

  return (
    <TribeRouter
      {...{
        location,
        params,
        Link: forwardRef<
          HTMLAnchorElement,
          ComponentProps<TribeRouterType['Link']>
        >(({ children, ...props }, ref) => (
          <Link ref={ref} {...props}>
            {children}
          </Link>
        )),
        Redirect: props => <Navigate to={props.to} {...props} />,
        redirect: useCallback(
          href => {
            const isInternal =
              href && href?.indexOf('/') === 0 && href?.indexOf('//') !== 0

            if (isInternal) {
              window.location.href = `${subfolder}${href}`
            } else {
              window.location.href = href
            }
          },
          [subfolder],
        ),
        push: useCallback(
          path => {
            navigate(path)
          },
          [navigate],
        ),
        replace: useCallback(
          path => {
            navigate(path, { replace: true })
          },
          [navigate],
        ),
      }}
    >
      {children}
      {otsm && <Otsm otsm={otsm} />}
    </TribeRouter>
  )
}
